import React, { useEffect, useState } from "react"
import { useLocation } from "@reach/router"
import { navigate } from "gatsby"
import { Helmet } from "react-helmet"
import cx from "clsx"
import FlashMessages from "components/FlashMessages"
import Footer from "components/Footer"
import GlobalAlerts from "components/GlobalAlerts"
import Header from "components/Header"
import Show from "components/Show"
import Sidebar from "components/Sidebar"
import Spinner from "components/Spinner"
import Toasts from "components/Toasts"
import { DatesProvider, DateRange } from "context/dates"
import { MenuProvider } from "context/menu"
import useAccountUser from "hooks/useAccountUser"
import useDesignerUser from "hooks/useDesignerUser"
import useIdentifySegmentUser from "hooks/useIdentifySegmentUser"
import { DateRangePreset } from "lib/dateRangePresets"

export interface LayoutProps {
  children: React.ReactNode
  title: string
  initialDateRange?: DateRange
  initialDateRangePresets?: DateRangePreset[]
}

const Layout = ({
  title,
  children,
  initialDateRange = undefined,
  initialDateRangePresets = undefined
}: LayoutProps) => {
  const { pathname } = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  useIdentifySegmentUser()

  const { accountUser } = useAccountUser()
  const { designerUser } = useDesignerUser()

  useEffect(() => {
    if (accountUser && accountUser?.isAnonymous) {
      navigate(`/login/?next=${pathname}`)
    }
  }, [accountUser, pathname])

  return (
    <>
      <Helmet defaultTitle="Brand Dashboard" titleTemplate="%s | Wolf & Badger">
        <html lang="en-us" />
        <title>{title}</title>
        <link rel="preconnect" href="https://use.typekit.net" />

        <meta name="apple-mobile-web-app-title" content="Brand Dashboard" />
        <meta name="apple-mobile-web-app-capable" content="no" />
        <link href="/staticmedia/i/favicons/favicon.png" rel="icon" />
        <link
          href="/staticmedia/i/favicons/apple-touch-icon.png"
          rel="apple-touch-icon"
        />
        <link
          href="/staticmedia/i/favicons/apple-touch-icon-76x76.png"
          rel="apple-touch-icon"
          sizes="76x76"
        />
        <link
          href="/staticmedia/i/favicons/apple-touch-icon-120x120.png"
          rel="apple-touch-icon"
          sizes="120x120"
        />
        <link
          href="/staticmedia/i/favicons/apple-touch-icon-152x152.png"
          rel="apple-touch-icon"
          sizes="152x152"
        />
        <link
          href="/staticmedia/i/favicons/apple-touch-icon-180x180.png"
          rel="apple-touch-icon"
          sizes="180x180"
        />
        <link
          href="/staticmedia/i/favicons/icon-hires.png"
          rel="icon"
          sizes="192x192"
        />
        <link
          href="/staticmedia/i/favicons/icon-normal.png"
          rel="icon"
          sizes="128x128"
        />
      </Helmet>

      <DatesProvider
        initialDateRange={initialDateRange}
        initialDateRangePresets={initialDateRangePresets}
      >
        <Show
          when={
            accountUser &&
            !accountUser?.isGuest &&
            !accountUser.isAnonymous &&
            designerUser
          }
          fallback={
            <div className="mt-32 flex justify-center max-w-xl mx-auto">
              <Spinner height={50} width={50} />
            </div>
          }
        >
          <Header setSidebarOpen={setSidebarOpen} />

          <MenuProvider>
            <Sidebar
              open={sidebarOpen}
              setOpen={setSidebarOpen}
              collapsed={sidebarCollapsed}
              setCollapsed={setSidebarCollapsed}
            />
          </MenuProvider>

          <main
            className={cx(`flex flex-col px-5 py-6 lg:px-14 lg:py-8`, {
              "md:ml-20": sidebarCollapsed,
              "md:ml-60": !sidebarCollapsed
            })}
          >
            <GlobalAlerts />
            <Toasts />
            <FlashMessages />
            {children}
          </main>

          <Footer />
        </Show>
      </DatesProvider>
    </>
  )
}

export default Layout
